/**
 * ApplyLeave.jsx — Iter 123. Single page where any employee can:
 *   • see their leave balance
 *   • apply for a new leave
 *   • see history + approval status
 *
 * Lives at `/hr/apply-leave` (sidebar Overview group, key='dashboard'
 * so it's accessible to every employee, not just HR).
 *
 * Mobile mirror at `/m/leave`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Calendar, Plus, X, FileText, Loader2, Undo2, User, ChevronDown, RefreshCw } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { formatDate } from '../../lib/format';

// LEAVE_TYPES are now loaded from GET /hr/leaves/policy (API-driven, not hardcoded).
// Fallback to a minimal set if the API fails.
const LEAVE_TYPES_FALLBACK = [
  { code: 'EL', label: 'Earned Leave',     paid: true, description: '6 days/year · paid' },
  { code: 'PL', label: 'Privilege Leave',  paid: true, description: '18 days/year · after probation · min 3 days' },
  { code: 'CO', label: 'Comp-off',         paid: true, description: 'Earned by working on Sundays/holidays' },
];

function _isProbation(emp) {
  if (!emp) return false;
  if ((emp.employment_status || '').toLowerCase() === 'confirmed') return false;
  // Compute probation end from doj + probation_period_days or probation_months
  const doj = emp.doj || emp.date_of_joining;
  if (!doj) return false;
  const dojDate = new Date(doj);
  let endDate = null;
  if (emp.probation_period_days) {
    endDate = new Date(dojDate.getTime() + emp.probation_period_days * 86400000);
  } else if (emp.probation_months) {
    endDate = new Date(dojDate);
    endDate.setMonth(endDate.getMonth() + Number(emp.probation_months));
  }
  if (!endDate) return false;
  return new Date() <= endDate;
}

function _probationEndStr(emp) {
  if (!emp) return '';
  const doj = emp.doj || emp.date_of_joining;
  if (!doj) return '';
  const dojDate = new Date(doj);
  let endDate = null;
  if (emp.probation_period_days) {
    endDate = new Date(dojDate.getTime() + emp.probation_period_days * 86400000);
  } else if (emp.probation_months) {
    endDate = new Date(dojDate);
    endDate.setMonth(endDate.getMonth() + Number(emp.probation_months));
  }
  return endDate ? endDate.toISOString().slice(0, 10) : '';
}

const STATUS_CLR = {
  Pending:  'bg-amber-50 text-amber-700 border-amber-200',
  Approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Rejected: 'bg-rose-50 text-rose-700 border-rose-200',
};

// Item 4 (30-Sep-2026 HR_Pending doc — HR-CR-02): per-leave-type ledger —
// opening, monthly credits, leave taken, adjustments, lapse/carry-forward —
// with a running balance, from GET /hr/leaves/ledger/entries.
const LEDGER_ENTRY_LABEL = {
  opening_balance: 'Opening Balance', conversion_opening: 'Opening Balance', accrual: 'Monthly Credit',
  leave_debit: 'Leave Taken', roster_leave: 'Leave Taken (Roster)',
  half_day_auto: 'Half-day Cover', leave_release: 'Leave Reversed',
  manual_adjustment: 'Adjustment', year_end_lapse: 'Year-end Lapse',
  year_end_carry_forward: 'Carried Forward',
  co_earned: 'Comp off earned', co_taken: 'Comp off taken', co_reversal: 'Comp off credited back',
  brought_forward: 'Brought forward', manual_credit: 'Manual credit',
};

function LeaveLedgerPanel({ employeeId, leaveTypes }) {
  const [open, setOpen] = useState(false);
  const [ltype, setLtype] = useState('EL');
  const [year, setYear] = useState(new Date().getFullYear());
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employeeId) return;
    setLoading(true);
    apiGet('/hr/leaves/ledger/entries', { employee_id: employeeId, year, leave_type: ltype })
      .then(r => setEntries(r?.entries || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [open, employeeId, year, ltype]);

  const thisYear = new Date().getFullYear();
  const ledgerTypes = ['EL', 'PL', 'CO'].map(c => leaveTypes.find(t => t.code === c) || LEAVE_TYPES_FALLBACK.find(t => t.code === c));

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="leave-ledger-panel">
      <button onClick={() => setOpen(o => !o)} className="w-full flex items-center justify-between" data-testid="leave-ledger-toggle">
        <div className="flex items-center gap-2">
          <FileText size={14} className="text-[#3b4872]" />
          <span className="kpi-overline">Leave Ledger</span>
        </div>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-3">
          <div className="flex gap-2 mb-3">
            <select value={ltype} onChange={(e) => setLtype(e.target.value)} data-testid="leave-ledger-type-select" className="px-2 py-1.5 border border-slate-300 rounded text-xs bg-white">
              {ledgerTypes.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
            <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="leave-ledger-year-select" className="px-2 py-1.5 border border-slate-300 rounded text-xs bg-white">
              {[thisYear, thisYear - 1, thisYear - 2].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="text-xs text-slate-400 flex items-center gap-1.5 py-3"><RefreshCw size={11} className="animate-spin" /> Loading…</div>
          ) : entries.length === 0 ? (
            <div className="text-xs text-slate-400 italic py-3 text-center">No ledger entries for {ltype} in {year}.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="leave-ledger-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left py-1.5 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Date</th>
                    <th className="text-left py-1.5 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Entry</th>
                    <th className="text-right py-1.5 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Credited</th>
                    <th className="text-right py-1.5 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Debited</th>
                    <th className="text-right py-1.5 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.id || i} className={i % 2 ? 'bg-slate-50/40' : ''} data-testid={`leave-ledger-row-${e.id || i}`}>
                      <td className="py-1.5 px-2 tabular-nums whitespace-nowrap">{formatDate(e.date || e.created_at)}</td>
                      <td className="py-1.5 px-2">{e.leave_type === 'CO' && e.note ? e.note : <>{LEDGER_ENTRY_LABEL[e.entry_type] || e.entry_type}{e.note ? <span className="text-slate-400"> — {e.note}</span> : ''}</>}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-emerald-700">{e.days_credited > 0 ? `+${e.days_credited}` : ''}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums text-rose-700">{e.days_debited > 0 ? `-${e.days_debited}` : ''}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums font-semibold">{e.affects_balance ? e.running_balance : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ApplyLeave({ compact = false }) {
  const [balance, setBalance] = useState([]);
  const [history, setHistory] = useState([]);
  const [profile, setProfile] = useState(null);
  const [leaveTypes, setLeaveTypes] = useState(LEAVE_TYPES_FALLBACK);
  const [shortLeaveUsage, setShortLeaveUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sandwichDates, setSandwichDates] = useState([]);     // Item 4: off-days needing their own leave type
  const [sandwichTypes, setSandwichTypes] = useState({});     // { date: leave_type }
  const [form, setForm] = useState({
    leave_type: 'EL',
    from_date: new Date().toISOString().slice(0, 10),
    to_date: new Date().toISOString().slice(0, 10),
    reason: '',
    half_day: false,
    half_day_period: 'AM',
  });

  const load = async () => {
    setLoading(true);
    try {
      const me = await apiGet('/hr/me');
      setProfile(me);
      apiGet('/hr/leaves/policy').then(p => {
        if (p?.policy) {
          const types = Object.entries(p.policy)
            .filter(([, v]) => v.ui_visible !== false && !v.retired)
            .map(([code, v]) => ({ code, label: v.name, paid: !v.no_pay, description: '' }));
          if (types.length > 0) setLeaveTypes(types);
        }
      }).catch(() => {});
      if (me?.linked) {
        const emp = me.employee || {};
        const [bal, leaves] = await Promise.all([
          apiGet('/hr/me/leave-balance').catch(() => ({ balance: [] })),
          apiGet('/hr/leaves').catch(() => []),
        ]);
        setBalance(bal.balance || []);
        const all = Array.isArray(leaves) ? leaves : (leaves.leaves || leaves.items || []);
        setHistory(all.filter(l => l.employee_id === emp.id || l.emp_code === emp.emp_id));
        apiGet('/hr/me/short-leave-usage').then(r => setShortLeaveUsage(r)).catch(() => {});
      }
    } catch { /* noop */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const emp = profile?.employee || {};
  const isProbation = _isProbation(emp);
  const probationEndDate = _probationEndStr(emp);
  // Item 5a (30-Sep-2026): Maternity Leave is not applicable to male employees.
  const visibleLeaveTypes = useMemo(
    () => leaveTypes.filter(t => t.code !== 'ML' || String(emp.gender || '').toLowerCase() === 'female'),
    [leaveTypes, emp.gender]
  );

  const daysCount = useMemo(() => {
    if (form.half_day) return form.from_date ? 0.5 : 0;
    if (!form.from_date || !form.to_date) return 0;
    const a = new Date(form.from_date);
    const b = new Date(form.to_date);
    if (b < a) return 0;
    return Math.round((b - a) / 86400000) + 1;
  }, [form.from_date, form.to_date, form.half_day]);

  // M6: during probation only CO / LWP may be applied — keep the picker on a valid type.
  useEffect(() => {
    if (isProbation && !['CO', 'LWP'].includes(form.leave_type)) {
      setForm(f => ({ ...f, leave_type: 'CO' }));
    }
  }, [isProbation, form.leave_type]);

  // Item 5b (30-Sep-2026): warn when the request exceeds what's actually available.
  const selectedBalance = balance.find(b => b.leave_type === form.leave_type);
  const availableForType = selectedBalance ? (selectedBalance.available ?? selectedBalance.remaining) : null;
  const exceedsAvailable = availableForType != null && daysCount > availableForType;

  // Item 4: a changed date range/type invalidates any previously-detected sandwich picks.
  useEffect(() => {
    setSandwichDates([]);
    setSandwichTypes({});
  }, [form.from_date, form.to_date, form.leave_type, form.half_day]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!profile?.linked) { toast.error('Your account is not linked to an employee record yet'); return; }
    const empRec = profile.employee || {};
    if (!form.reason.trim()) { toast.error('Please provide a reason for the leave'); return; }
    if (daysCount <= 0) { toast.error('Invalid date range'); return; }
    if (isLongLeave && !showLongLeaveConfirm) { setShowLongLeaveConfirm(true); return; }
    if (sandwichDates.length > 0 && sandwichDates.some(d => !sandwichTypes[d])) {
      toast.error('Pick a leave type for every sandwiched day below');
      return;
    }
    setSubmitting(true);
    // During probation, PL is not available (enforced by backend policy)
    // but we no longer force LWP — employee may still use EL or CO
    const leaveType = form.leave_type;
    try {
      const payload = {
        employee_id: empRec.id,
        emp_code: empRec.emp_id,
        employee_name: empRec.name,
        leave_type: leaveType,
        from_date: form.from_date,
        to_date: form.half_day ? form.from_date : form.to_date,
        days: daysCount,
        reason: form.reason,
        status: 'Pending',
        half_day: form.half_day,
        half_day_period: form.half_day ? form.half_day_period : null,
      };
      if (sandwichDates.length > 0) payload.sandwich_leave_types = sandwichTypes;
      await apiPost('/hr/leaves', payload);
      toast.success(`Leave request submitted (${daysCount} day${daysCount !== 1 ? 's' : ''})`);
      setShowForm(false);
      setShowLongLeaveConfirm(false);
      setSandwichDates([]);
      setSandwichTypes({});
      setForm({ leave_type: 'EL', from_date: new Date().toISOString().slice(0, 10), to_date: new Date().toISOString().slice(0, 10), reason: '', half_day: false, half_day_period: 'AM' });
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === 'sandwich_leave_type_required' && Array.isArray(detail.dates)) {
        setSandwichDates(detail.dates);
        setSandwichTypes(t => {
          const next = { ...t };
          detail.dates.forEach(d => { if (!next[d]) next[d] = form.leave_type; });
          return next;
        });
        toast.error('Pick a leave type for the sandwiched day(s) below, then submit again');
      } else {
        toast.error((typeof detail === 'string' ? detail : detail?.message) || 'Failed to submit leave');
      }
    }
    setSubmitting(false);
  };

  const [withdrawingId, setWithdrawingId] = useState(null);
  const isLongLeave = daysCount > 5;
  const [showLongLeaveConfirm, setShowLongLeaveConfirm] = useState(false);
  const withdraw = async (l) => {
    const isFuturePending = l.status === 'Pending';
    if (!window.confirm(isFuturePending ? 'Withdraw this pending leave request?' : 'Request cancellation of this approved leave?')) return;
    setWithdrawingId(l.id);
    try {
      const r = await apiPut(`/hr/leaves/${l.id}/withdraw`, {});
      toast.success(r?.message || 'Request updated');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to withdraw/cancel');
    }
    setWithdrawingId(null);
  };

  if (loading) return <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>;
  if (!profile?.linked) return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="bg-amber-50 border border-amber-200 rounded-md p-5 text-sm text-amber-800">
        <b>Your account isn't linked to an employee record yet.</b><br />
        Please ask HR to link your account to apply for leaves.
      </div>
    </div>
  );

  return (
    <div className={compact ? "p-3" : "p-4 md:p-8 max-w-5xl mx-auto"} data-testid="apply-leave-page">
      {!compact && (
        <PageHeader
          overline="Self Service · Employee"
          title="Apply for Leave"
          subtitle={`Hi ${(profile.employee?.name || '').split(' ')[0]} — request a leave, track approval status, and check your remaining balance.`}
          actions={
            <button onClick={() => setShowForm(true)} data-testid="apply-leave-new-btn" className="flex items-center gap-2 px-4 py-2.5 bg-[#3b4872] text-white text-sm font-display font-semibold rounded-sm hover:bg-[#2c3658]">
              <Plus size={15} /> New Leave Request
            </button>
          }
        />
      )}
      {compact && (
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-base">Apply for Leave</h2>
          <button onClick={() => setShowForm(true)} data-testid="apply-leave-new-btn" className="px-3 py-1.5 bg-[#3b4872] text-white text-xs font-display font-semibold rounded-sm flex items-center gap-1.5">
            <Plus size={12} /> New
          </button>
        </div>
      )}

      {/* Balance */}
      <div className="bg-white border border-slate-200 rounded-md p-4 mb-4">
        <div className="flex items-center gap-2 mb-2 justify-between">
          <div className="flex items-center gap-2">
            <Calendar size={14} className="text-[#3b4872]" />
            <div className="kpi-overline">Leave Balance · {new Date().getFullYear()}</div>
          </div>
          {shortLeaveUsage && (
            <div className="text-[10px] text-slate-500" data-testid="apply-leave-short-leave-usage">
              Short leaves used: <b className="text-slate-700">{shortLeaveUsage.used} of {shortLeaveUsage.quota}</b>
            </div>
          )}
        </div>
        {balance.length === 0 ? (
          <div className="text-xs text-slate-400 italic">No leave policy assigned yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {balance.map((b) => {
              const accruing = isProbation && (b.leave_type === 'PL' || b.leave_type === 'EL');
              return (
                <div key={b.leave_type} className="border border-slate-200 rounded p-3 bg-slate-50/40" data-testid={`leave-balance-${b.leave_type}`}>
                  <div className="text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">{b.leave_type}</div>
                  <div className="font-display font-bold text-xl mt-1 tabular-nums" data-testid={`leave-available-${b.leave_type}`}>{b.available ?? b.remaining}</div>
                  <div className="text-[10px] text-slate-500">available</div>
                  {accruing ? (
                    <div className="text-[10px] text-amber-700 mt-1 leading-snug" data-testid={`leave-accruing-${b.leave_type}`}>
                      held during probation · {b.remaining} accrued so far
                    </div>
                  ) : (
                    <div className="text-[10px] text-slate-600 mt-1 space-y-0.5 leading-snug">
                      <div>Accrued: <b className="tabular-nums">{b.quota ?? '—'}</b></div>
                      <div>Used: <b className="tabular-nums">{b.used}</b></div>
                      {b.pending > 0 && <div className="text-amber-700">Pending: <b className="tabular-nums">{b.pending}</b></div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Item 4 (30-Sep-2026 HR_Pending doc): per-leave-type ledger */}
      <LeaveLedgerPanel employeeId={emp.id} leaveTypes={visibleLeaveTypes.length ? visibleLeaveTypes : LEAVE_TYPES_FALLBACK} />

      {/* History */}
      <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="apply-leave-history">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <FileText size={14} className="text-[#3b4872]" />
            <div className="kpi-overline">Your Leave Requests · {history.length}</div>
          </div>
        </div>
        {history.length === 0 ? (
          <div className="text-xs text-slate-400 italic py-6 text-center">No leave requests yet. Click "New Leave Request" to apply.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Type</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">From</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">To</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Days</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Reason</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Status</th>
                  <th className="text-left py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Approver</th>
                  <th className="text-right py-2 px-2 text-[10px] uppercase tracking-widest font-display font-bold text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody>
                {history.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || '')).map((l, i) => {
                  const canWithdraw = l.status === 'Pending';
                  const canCancel = l.status === 'Approved' && l.from_date > new Date().toISOString().slice(0, 10);
                  return (
                  <tr key={l.id || i} className={i % 2 ? 'bg-slate-50/40' : ''} data-testid={`leave-history-row-${l.id || i}`}>
                    <td className="py-2 px-2 font-display font-semibold">{l.leave_type}{l.half_day && <span className="text-[9px] text-indigo-600 ml-1">½ day</span>}</td>
                    <td className="py-2 px-2 tabular-nums">{formatDate(l.from_date)}</td>
                    <td className="py-2 px-2 tabular-nums">{formatDate(l.to_date)}</td>
                    <td className="py-2 px-2 text-right tabular-nums">{l.days}</td>
                    <td className="py-2 px-2 max-w-[200px] truncate" title={l.reason}>{l.reason}</td>
                    <td className="py-2 px-2"><span className={`tag border ${STATUS_CLR[l.status] || 'bg-slate-50 text-slate-600 border-slate-200'}`}>{l.status || 'Pending'}</span></td>
                    <td className="py-2 px-2 text-xs text-slate-500">{l.approver || '—'}</td>
                    <td className="py-2 px-2 text-right">
                      {(canWithdraw || canCancel) && (
                        <button
                          onClick={() => withdraw(l)}
                          disabled={withdrawingId === l.id}
                          data-testid={`leave-withdraw-btn-${l.id}`}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-display font-semibold border border-rose-200 text-rose-700 rounded hover:bg-rose-50 disabled:opacity-50"
                        >
                          <Undo2 size={10} /> {withdrawingId === l.id ? '…' : canWithdraw ? 'Withdraw' : 'Cancel'}
                        </button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal — apply form */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end md:items-center justify-center md:p-4">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full md:max-w-lg md:rounded-md flex flex-col max-h-[92vh]">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display font-bold text-lg">New Leave Request</h3>
              <button onClick={() => setShowForm(false)} data-testid="apply-leave-close-btn" className="w-9 h-9 flex items-center justify-center hover:bg-slate-100 rounded"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="p-4 space-y-4 overflow-y-auto">
              {isProbation && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="probation-banner">
                  <strong>You are in probation period{probationEndDate ? ` (ends ${probationEndDate})` : ''}.</strong><br />
                  During probation you can apply for <strong>Comp-Off (CO)</strong> and <strong>Leave Without Pay (LWP)</strong> only. Privilege Leave (PL), Earned Leave (EL) and Maternity become available after confirmation.
                </div>
              )}
              {/* Item 5 (30-Sep-2026 HR_Pending doc — HR-CR-04): show who approves */}
              <div className="flex items-center gap-1.5 text-[11px] text-slate-500" data-testid="apply-leave-approver-line">
                <User size={11} className="text-[#3b4872]" />
                Goes to: <b className="text-slate-700">{emp.reporting_manager_name || 'your reporting manager'}</b> and the HR team
              </div>
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Leave Type</label>
                <select value={form.leave_type} onChange={(e) => setForm({ ...form, leave_type: e.target.value })} data-testid="apply-leave-type" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm bg-white">
                  {visibleLeaveTypes.filter(t => !isProbation || ['CO', 'LWP'].includes(t.code)).map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">From</label>
                  <input type="date" value={form.from_date} onChange={(e) => setForm({ ...form, from_date: e.target.value, to_date: form.half_day ? e.target.value : form.to_date })} data-testid="apply-leave-from" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" required />
                </div>
                <div>
                  <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">To</label>
                  <input type="date" value={form.half_day ? form.from_date : form.to_date} min={form.from_date} disabled={form.half_day} onChange={(e) => setForm({ ...form, to_date: e.target.value })} data-testid="apply-leave-to" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm disabled:bg-slate-100" required />
                </div>
              </div>
              {form.leave_type !== 'ML' && (
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 text-xs text-slate-700">
                    <input type="checkbox" checked={form.half_day} onChange={(e) => setForm({ ...form, half_day: e.target.checked, to_date: e.target.checked ? form.from_date : form.to_date })} data-testid="apply-leave-half-day-checkbox" />
                    Half day
                  </label>
                  {form.half_day && (
                    <select value={form.half_day_period} onChange={(e) => setForm({ ...form, half_day_period: e.target.value })} data-testid="apply-leave-half-day-period" className="px-2 py-1.5 border border-slate-300 rounded text-xs">
                      <option value="AM">Morning (AM)</option>
                      <option value="PM">Afternoon (PM)</option>
                    </select>
                  )}
                </div>
              )}
              <div className="bg-indigo-50/40 border border-indigo-200 rounded p-2 text-xs text-slate-700">
                <b>{daysCount}</b> day{daysCount !== 1 ? 's' : ''} requested.
                {daysCount > 0 && form.leave_type && leaveTypes.find(t => t.code === form.leave_type)?.paid && <span className="text-emerald-700 ml-2">· Will be auto-marked as Paid Leave in attendance on approval.</span>}
                {daysCount > 0 && form.leave_type && !leaveTypes.find(t => t.code === form.leave_type)?.paid && <span className="text-rose-700 ml-2">· Will be counted as LOP (Loss of Pay).</span>}
              </div>
              {exceedsAvailable && (
                <div className="p-2.5 bg-rose-50 border border-rose-200 rounded text-xs text-rose-800 flex items-start gap-2" data-testid="apply-leave-exceeds-warning">
                  <span>⚠</span>
                  <span>You're requesting <b>{daysCount}</b> day{daysCount !== 1 ? 's' : ''} but only <b>{availableForType}</b> {form.leave_type} {availableForType === 1 ? 'day is' : 'days are'} available. This request may be rejected or fall back to LOP for the excess.</span>
                </div>
              )}
              {isLongLeave && (
                <div className="p-2.5 bg-amber-50 border border-amber-200 rounded text-xs text-amber-900" data-testid="apply-leave-long-leave-banner">
                  This is a long leave. Please consult your supervisor and the HR team before applying.
                </div>
              )}
              {sandwichDates.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-300 rounded space-y-2" data-testid="apply-leave-sandwich-panel">
                  <p className="text-xs text-amber-900">
                    {sandwichDates.map(d => formatDate(d)).join(', ')} {sandwichDates.length > 1 ? 'fall' : 'falls'} between leave on both sides and {sandwichDates.length > 1 ? "aren't" : "isn't"} a paid day off anymore. Pick a leave type for {sandwichDates.length > 1 ? 'each' : 'it'}:
                  </p>
                  {sandwichDates.map(d => (
                    <div key={d} className="flex items-center gap-2">
                      <span className="text-xs font-display font-semibold text-amber-900 w-24 shrink-0">{formatDate(d)}</span>
                      <select
                        value={sandwichTypes[d] || ''}
                        onChange={(e) => setSandwichTypes(t => ({ ...t, [d]: e.target.value }))}
                        data-testid={`apply-leave-sandwich-type-${d}`}
                        className="flex-1 px-2 py-1.5 border border-amber-300 rounded text-xs bg-white"
                      >
                        <option value="">Select type…</option>
                        {visibleLeaveTypes.map(t => <option key={t.code} value={t.code}>{t.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
              )}
              <div>
                <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">Reason</label>
                <textarea value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="apply-leave-reason" rows={3} placeholder="Brief reason for the leave" className="w-full px-3 py-2.5 border border-slate-300 rounded text-sm" required />
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-200" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-sm border border-slate-300 rounded font-display">Cancel</button>
                <button type="submit" disabled={submitting} data-testid="apply-leave-submit-btn" className="px-5 py-2 text-sm bg-[#3b4872] text-white rounded font-display font-semibold disabled:opacity-50 flex items-center gap-2">
                  {submitting && <Loader2 size={12} className="animate-spin" />}
                  {submitting ? 'Submitting…' : 'Submit Request'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showLongLeaveConfirm && (
        <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowLongLeaveConfirm(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-sm rounded-md" data-testid="apply-leave-long-leave-confirm-modal">
            <div className="p-4 space-y-2">
              <h3 className="font-display font-bold text-base text-amber-800">Long Leave — {daysCount} days</h3>
              <p className="text-sm text-slate-700">This is a long leave. Please consult your supervisor and the HR team before applying.</p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
              <button type="button" onClick={() => setShowLongLeaveConfirm(false)} data-testid="apply-leave-long-leave-cancel-btn" className="px-4 py-2 text-sm border border-slate-300 rounded font-display">Cancel</button>
              <button type="button" onClick={submit} disabled={submitting} data-testid="apply-leave-long-leave-continue-btn" className="px-5 py-2 text-sm bg-amber-700 text-white rounded font-display font-semibold disabled:opacity-50">
                {submitting ? 'Submitting…' : 'Continue'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
